/**
 * useEventStream — SSE consumer that supports custom headers.
 *
 * Why not the native EventSource API?
 *   EventSource doesn't allow Authorization headers. Workarounds:
 *     - Token in query string (leaks to logs)
 *     - Polyfill (extra dep)
 *     - fetch() + ReadableStream (this implementation)
 *
 * This hook opens a long-lived fetch request, parses SSE frames manually,
 * dispatches events to a handler, and reconnects with backoff on disconnect.
 *
 * It integrates with the api-client's JWT refresh flow: on a 401, we let
 * the api-client refresh the token, then we reconnect.
 *
 * Usage:
 *   useEventStream({
 *     path: '/sessions/stream',
 *     onEvent: (type, data) => { ... },
 *     enabled: true,
 *   });
 */

import { useEffect, useRef } from 'react';
import { useAuthStore } from '@/lib/auth-store';

const API_BASE = (import.meta.env.VITE_API_BASE as string | undefined) ?? '/api';

type EventHandler = (event: string, data: unknown) => void;

interface UseEventStreamOptions {
  /** Relative API path, e.g. '/sessions/stream'. */
  path: string;
  /** Called for each SSE event. */
  onEvent: EventHandler;
  /** Toggle to pause/resume. Default true. */
  enabled?: boolean;
}

interface ConnectState {
  abort: AbortController;
  /** Set to true when the hook is unmounted — prevents reconnection. */
  cancelled: boolean;
  /** Backoff in ms, doubles on each consecutive failure (capped at 30s). */
  retryDelay: number;
}

export function useEventStream({ path, onEvent, enabled = true }: UseEventStreamOptions): void {
  // Keep the handler in a ref so we don't re-open the connection when the
  // parent re-renders with a new closure.
  const handlerRef = useRef(onEvent);
  handlerRef.current = onEvent;

  useEffect(() => {
    if (!enabled) return;

    const state: ConnectState = {
      abort: new AbortController(),
      cancelled: false,
      retryDelay: 1000,
    };

    async function connect(): Promise<void> {
      while (!state.cancelled) {
        try {
          await openOnce(state, path, handlerRef);
          // openOnce only returns on clean disconnect. Treat as needing reconnect.
          if (!state.cancelled) {
            await sleep(state.retryDelay);
            state.retryDelay = Math.min(state.retryDelay * 2, 30_000);
          }
        } catch (err) {
          if (state.cancelled) return;
          // AbortError just means we cancelled — exit silently.
          if (err instanceof DOMException && err.name === 'AbortError') return;
          console.warn('[useEventStream] error, will retry:', err);
          await sleep(state.retryDelay);
          state.retryDelay = Math.min(state.retryDelay * 2, 30_000);
        }
      }
    }

    void connect();

    return () => {
      state.cancelled = true;
      state.abort.abort();
    };
  }, [path, enabled]);
}

async function openOnce(state: ConnectState, path: string, handlerRef: React.MutableRefObject<EventHandler>): Promise<void> {
  const { accessToken, orgSlug } = useAuthStore.getState();
  if (!accessToken || !orgSlug) {
    // No session — wait and let the auth flow react. We don't crash the loop.
    await sleep(2000);
    return;
  }

  const url = `${API_BASE}${path}`;
  const res = await fetch(url, {
    method: 'GET',
    headers: {
      Accept: 'text/event-stream',
      Authorization: `Bearer ${accessToken}`,
      'X-Org-Slug': orgSlug,
    },
    signal: state.abort.signal,
  });

  if (res.status === 401) {
    // Token expired. Trigger a refresh via a small unrelated request through
    // the api-client. The next loop iteration will pick up the fresh token.
    try {
      const { api } = await import('@/lib/api-client');
      await api.get('/auth/me');
    } catch {
      // ignore — auth-store will be cleared by api-client if refresh fails
    }
    return;
  }

  if (!res.ok || !res.body) {
    throw new Error(`SSE connect failed: ${res.status}`);
  }

  // Successful connect resets the backoff
  state.retryDelay = 1000;

  // Parse SSE frames as they arrive
  const reader = res.body.pipeThrough(new TextDecoderStream()).getReader();
  let buffer = '';
  let eventName = 'message';
  let dataLines: string[] = [];

  try {
    while (true) {
      const { value, done } = await reader.read();
      if (done) break;
      buffer += value;

      // SSE frames are separated by blank lines. Split greedily.
      let nl: number;
      while ((nl = buffer.indexOf('\n')) !== -1) {
        const line = buffer.slice(0, nl).replace(/\r$/, '');
        buffer = buffer.slice(nl + 1);

        if (line === '') {
          // End of frame: dispatch
          if (dataLines.length > 0) {
            const raw = dataLines.join('\n');
            let parsed: unknown = raw;
            try {
              parsed = JSON.parse(raw);
            } catch {
              // keep as string if not JSON
            }
            try {
              handlerRef.current(eventName, parsed);
            } catch (err) {
              console.error('[useEventStream] handler error', err);
            }
          }
          eventName = 'message';
          dataLines = [];
          continue;
        }

        if (line.startsWith(':')) {
          // Comment/heartbeat — ignored
          continue;
        }
        if (line.startsWith('event:')) {
          eventName = line.slice(6).trim();
          continue;
        }
        if (line.startsWith('data:')) {
          dataLines.push(line.slice(5).trimStart());
          continue;
        }
        // id: and retry: are valid SSE fields, ignored for our use case
      }
    }
  } finally {
    try {
      reader.releaseLock();
    } catch {
      // ignore
    }
  }
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}
